import moment from 'moment';
import { FinanceProgram, MarkupType } from '@trnsact/trnsact-shared-types';
import { LenderProfiles } from '@trnsact/trnsact-shared-types/src/generated';
import { FinanceProgramForm } from '../schema';
import { LenderOptions, MarkupMethods, Structures } from '../types';
import { mapCriteriaFormRulesToValue } from './mapCriteriaFormRulesToValue';

export function mapFinanceProgramEntityToFormValues(
  financeProgramEntity: FinanceProgram,
  lenderProfiles: LenderProfiles[],
  dealerProfiles: any[] = []
): FinanceProgramForm['program'] {
  const isSpecifyRate =
    new Set(financeProgramEntity?.paymentOptionsConfiguration?.terms?.map((value: any) => value.rate.toString()) ?? [])
      .size > 1;

  const defineStructureValue = () => {
    if (financeProgramEntity?.paymentOptionsConfiguration?.isSubsidized) return Structures.Subsidy;
    if (financeProgramEntity?.paymentOptionsConfiguration?.dealerMarkup) return Structures.Markup;
    if (financeProgramEntity?.paymentOptionsConfiguration?.customerRate) return Structures.Subsidy;

    return Structures.None;
  };

  return {
    id: financeProgramEntity?.financeProgramId ?? null,
    first: {
      dealers: dealerProfiles
        ? dealerProfiles?.reduce<any[]>((acc, dealerProfile) => {
            const isIncluded = !!financeProgramEntity?.financeProgramToLenderProfiles
              ?.map(value => value?.lenderProfileId)
              .includes(dealerProfile.lenderProfiles[0]?.lenderProfileId ?? '');

            if (isIncluded) {
              acc.push({
                ...dealerProfile,
                value: dealerProfile.id,
                label: dealerProfile.name.replace('VP - ', ''),
              });
            }

            return acc;
          }, [])
        : [],
      name: financeProgramEntity?.nameInternal ?? '',
      notes: financeProgramEntity?.paymentOptionsConfiguration?.notes ?? '',
      guidelines: financeProgramEntity?.paymentOptionsConfiguration?.guidelines,
      displayName: financeProgramEntity?.paymentOptionsConfiguration?.financeProgramName ?? '',
      lenders: lenderProfiles?.reduce<LenderOptions[]>((acc, lenderProfile) => {
        const isIncluded = !!financeProgramEntity?.financeProgramToLenderProfiles
          ?.map(value => value?.lenderProfileId)
          .includes(lenderProfile?.id ?? '');

        if (isIncluded) {
          acc.push({
            ...lenderProfile,
            value: lenderProfile.id!,
            label: lenderProfile.lenderName!,
          });
        }

        return acc;
      }, []),
      linkToLenderCreditApp: !!financeProgramEntity?.paymentOptionsConfiguration?.linkToLenderCreditApp,
    },
    second: {
      isSpecifyRate,
      method: MarkupMethods.Markup,
      isSubsidized: financeProgramEntity?.paymentOptionsConfiguration?.isSubsidized ?? false,
      structure: defineStructureValue(),
      programType: financeProgramEntity?.paymentOptionsConfiguration?.type,
      rateType: financeProgramEntity?.paymentOptionsConfiguration?.rateType,
      rate: financeProgramEntity?.paymentOptionsConfiguration?.terms?.[0]?.rate,
      residual: financeProgramEntity?.paymentOptionsConfiguration?.residualPercentage ?? 0,
      customerRate: financeProgramEntity?.paymentOptionsConfiguration?.customerRate ?? 0,
      dealerMarkup: financeProgramEntity?.paymentOptionsConfiguration?.dealerMarkup ?? 0,
      isValidityDates: !!financeProgramEntity?.validDateEnd && !!financeProgramEntity?.validDateStart,
      paymentCalculationMethod: financeProgramEntity?.paymentOptionsConfiguration?.paymentPeriodTiming,
      advancePayment: financeProgramEntity?.paymentOptionsConfiguration?.numberOfAdvancePayments?.toString(),
      daysToFirstPayment: financeProgramEntity?.paymentOptionsConfiguration?.daysToFirstPayment?.toString(),
      terms: financeProgramEntity?.paymentOptionsConfiguration?.terms?.map((value: any) => value.term.toString()),
      minimumRequiredDownPayment: financeProgramEntity?.paymentOptionsConfiguration?.minimumRequiredDownPayment ?? 0,
      endDate: financeProgramEntity?.validDateEnd ? moment(Number(financeProgramEntity.validDateEnd)).toDate() : null,
      specifyRates: financeProgramEntity?.paymentOptionsConfiguration?.terms?.map((value: any) => ({
        ...value,
        term: value.term.toString(),
      })),
      fee: [
        ...(financeProgramEntity?.paymentOptionsConfiguration?.flatFees?.map((f: any) =>
          mapFeeValue(f, MarkupType.Flat)
        ) ?? []),
        ...(financeProgramEntity?.paymentOptionsConfiguration?.percentageFees?.map((f: any) =>
          mapFeeValue(f, MarkupType.Percentage)
        ) ?? []),
      ],
      startDate: financeProgramEntity?.validDateStart
        ? moment(Number(financeProgramEntity.validDateStart)).toDate()
        : null,
      configureSubsidyCap: !!financeProgramEntity?.paymentOptionsConfiguration?.subsidyCapConfiguration,
      subsidyCapConfiguration: financeProgramEntity?.paymentOptionsConfiguration?.subsidyCapConfiguration,
      specifyLenderMarketRates: financeProgramEntity?.paymentOptionsConfiguration?.specifyLenderMarketRates ?? [],
      tiers: financeProgramEntity?.scoreTiers?.tiers ?? null,
      isTiersEnabled: financeProgramEntity?.paymentOptionsConfiguration?.isTiersEnabled ?? false,
    },
    third: {
      criteria: financeProgramEntity?.prescreenCriteria?.prescreenCriteriaId
        ? {
            label: financeProgramEntity.prescreenCriteria.name,
            value: financeProgramEntity.prescreenCriteria.prescreenCriteriaId,
          }
        : null,
      conditions: mapCriteriaFormRulesToValue(
        financeProgramEntity?.prescreenCriteria?.jsonCriteria?.formRules,
        dealerProfiles
      ),
    },
    fourth: {
      submissionSettings: {
        excludedDocuments: financeProgramEntity.submissionSettings?.excludedDocuments ?? [],
        autoSubmitUserProfileId: financeProgramEntity.submissionSettings?.autoSubmitUserProfileId ?? '',
      },
    },
    fifth: {
      insuranceCoverage: {
        coverageRequirements: financeProgramEntity?.insuranceCoverage?.coverageRequirements,
        certificateRequirements: financeProgramEntity?.insuranceCoverage?.certificateRequirements,
        otherInstructions: financeProgramEntity?.insuranceCoverage?.otherInstructions,
      },
    },
    sixth: {},
  };
}

const mapFeeValue = ({ fee, financed, identifier }: any, type: MarkupType) => ({
  amount: fee,
  isFinanced: financed,
  description: identifier,
  type,
});
